import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { Label } from '@/design_system/Label/Label';
import { Separator } from '@/design_system/Menu/Menu';
import { RadioItemYesNo } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';
import { type Brand } from '@/models/brand';

import { type DefectBrandValue } from './DefectsTable';

export const DefectBrandsInputs = ({
  brands,
  setBrands,
  isDisabled,
  brandOptions: brandOptions = [],
  showErrors,
}: {
  brands: DefectBrandValue[];
  setBrands: (brands: DefectBrandValue[]) => void;
  isDisabled?: boolean;
  brandOptions?: Brand[];
  showErrors: boolean;
}) => {
  return (
    <Stack gap="0.5rem">
      <InlineText weight="medium">
        <Trans id="settings.defects.drawer.brands">Brands</Trans>
      </InlineText>

      <Stack gap="1rem">
        {brands.map((brand) => {
          const lastBrandOfList = brands.at(-1);
          const isLastBrand = lastBrandOfList?.id === brand.id;

          return (
            <>
              <DefectBrandInputs
                key={brand.id}
                brand={brand}
                brands={brands}
                setBrands={setBrands}
                brandOptions={brandOptions}
                showErrors={showErrors}
              />

              {!isLastBrand && <Separator />}
            </>
          );
        })}
      </Stack>

      {!isDisabled && (
        <AddBrandButton brands={brands} setBrands={setBrands} brandOptions={brandOptions} />
      )}
    </Stack>
  );
};

const DefectBrandInputs = ({
  brand,
  brands,
  setBrands,
  isDisabled,
  brandOptions: brandOptions = [],
  showErrors,
}: {
  brand: DefectBrandValue;
  brands: DefectBrandValue[];
  setBrands: (brands: DefectBrandValue[]) => void;
  isDisabled?: boolean;
  brandOptions?: Brand[];
  showErrors?: boolean;
}) => {
  return (
    <Stack row gap="2rem" alignItems="start" style={{ flex: 1 }}>
      <Stack row gap="0.5rem" alignItems="end" style={{ flex: 1 }}>
        {!isDisabled && <RemoveBrandButton brand={brand} brands={brands} setBrands={setBrands} />}

        <BrandInput
          brand={brand}
          brands={brands}
          setBrands={setBrands}
          brandOptions={brandOptions}
          showErrors={showErrors}
        />
      </Stack>

      <BrandResponsibilityRadioButtons brand={brand} brands={brands} setBrands={setBrands} />

      <RemovesAllBrandResponsibilityRadioButtons
        brand={brand}
        brands={brands}
        setBrands={setBrands}
      />
    </Stack>
  );
};

const RemoveBrandButton = ({
  brand,
  brands,
  setBrands,
}: {
  brand: DefectBrandValue;
  brands: DefectBrandValue[];
  setBrands: (brands: DefectBrandValue[]) => void;
}) => {
  const { t } = useLingui();

  return (
    <Button
      ariaLabel={t({
        id: 'settings.defects.drawer.brands.brand.delete',
        message: 'Remove',
      })}
      tooltip={t({
        id: 'settings.defects.drawer.brands.brand.delete',
        message: 'Remove',
      })}
      iconOnly
      size="large"
      variant="secondary-danger"
      onPress={() => setBrands(brands.filter((b) => b.id !== brand.id))}
    >
      <IconTrash />
    </Button>
  );
};

const BrandInput = ({
  brand,
  brands,
  setBrands,
  isDisabled,
  brandOptions: brandOptions = [],
  showErrors,
}: {
  brand: DefectBrandValue;
  brands: DefectBrandValue[];
  setBrands: (brands: DefectBrandValue[]) => void;
  isDisabled?: boolean;
  brandOptions?: Brand[];
  showErrors?: boolean;
}) => {
  const { t } = useLingui();

  const brandLabel = t({
    id: 'settings.defects.drawer.brands.brand.label',
    message: 'Brand',
  });

  const brandIds = brands.map((brand) => brand.brandId);
  const fitleredBrandOptions = brandOptions.filter((option) => !brandIds.includes(option.id));

  return (
    <InputSelect
      label={brandLabel}
      isSearchable
      aria-label={brandLabel}
      placeholder={brandLabel}
      value={brandOptions.find((option) => brand.brandId === option.id)}
      options={fitleredBrandOptions}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.name}
      onChange={(option) => {
        return setBrands(
          brands.map((b) => (b.id === brand.id ? { ...b, brandId: option!.id } : b))
        );
      }}
      isInvalid={showErrors && brand.brandId === 'tmp'}
      isDisabled={isDisabled}
      style={{ flex: 1 }}
    />
  );
};

const BrandResponsibilityRadioButtons = ({
  brand,
  brands,
  setBrands,
}: {
  brand: DefectBrandValue;
  brands: DefectBrandValue[];
  setBrands: (brands: DefectBrandValue[]) => void;
}) => {
  const { t } = useLingui();

  return (
    <Label
      label={t({
        id: 'settings.defects.drawer.brands.brand.brand-responsibility',
        message: 'Brand responsibility',
      })}
    >
      <RadioItemYesNo
        name="brand-responsibility"
        value={brand.brandResponsibility ? 'yes' : 'no'}
        onChange={(value) => {
          const booleanValue = value === 'yes' ? true : false;

          return setBrands(
            brands.map((b) => (b.id === brand.id ? { ...b, brandResponsibility: booleanValue } : b))
          );
        }}
      />
    </Label>
  );
};

const RemovesAllBrandResponsibilityRadioButtons = ({
  brand,
  brands,
  setBrands,
}: {
  brand: DefectBrandValue;
  brands: DefectBrandValue[];

  setBrands: (brands: DefectBrandValue[]) => void;
}) => {
  const { t } = useLingui();

  return (
    <Label
      label={t({
        id: 'settings.defects.drawer.brands.brand.removes-all-brand-responsibility',
        message: 'Removes all brand responsibility',
      })}
    >
      <RadioItemYesNo
        name="removes-all-brand-responsibility"
        value={brand.removesAllBrandResponsibility ? 'yes' : 'no'}
        onChange={(value) => {
          const booleanValue = value === 'yes' ? true : false;

          return setBrands(
            brands.map((b) =>
              b.id === brand.id ? { ...b, removesAllBrandResponsibility: booleanValue } : b
            )
          );
        }}
      />
    </Label>
  );
};

const AddBrandButton = ({
  brands,
  setBrands,
  isDisabled,
  brandOptions: brandOptions = [],
}: {
  brands: DefectBrandValue[];
  setBrands: (brands: DefectBrandValue[]) => void;
  isDisabled?: boolean;
  brandOptions?: Brand[];
}) => {
  return (
    <Button
      variant="link-subtle"
      size="small"
      style={{ alignSelf: 'start' }}
      onPress={() => {
        if (!brandOptions.length) return;

        return setBrands([
          ...brands,
          {
            id: crypto.randomUUID(),
            brandId: 'tmp',
            brandResponsibility: false,
            removesAllBrandResponsibility: false,
          },
        ]);
      }}
      disabled={isDisabled}
    >
      <IconAdd />
      <Trans id="settings.defects.drawer.brands.add">Add a brand</Trans>
    </Button>
  );
};
